import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";
import { environment } from "@env";

import {
  ClientDetailsDTO,
  ClientSimplifiedListDTO
} from "@shared/models/client-details-dto";
import { BlockDTO } from "@shared/models/block-dto";
import { SaveClientDTO } from "@shared/models/save-client-dto";
import {
  SearchFormDTO,
  SearchFormSimplifiedListDTO
} from "src/app/modules/private/modules/client/models/search-form-dto";
import { CarWorkshopDTO } from "@shared/models/car-workshop-dto";
import {
  GetClientApprovalUserDTO,
  GetClientRequestingUserDTO,
  PostClientApprovalUserDTO,
  PostClientRequestingUserDTO
} from "@shared/models/client-user-dto";
import {
  GetWorkflowApprovalLimitsDTO,
  UpdateWorkflowApprovalLimitsDTO,
  UpdateWorkflowConfigurationsDTO,
  WalletCarworkshopDTO
} from "@core/dtos";
import { PageManager } from "@shared/models/page-manager";
import {
  ClientBillingHistoryDTO,
  ClientBillingHistoryResponse
} from "@shared/models/client-billing-history-dto";
import { ClientFeeHistoryResponse } from "@shared/models/client-fee-model-history-dto";
import { ClientAdminFeeHistoryResponse } from "@shared/models/client-admin-fee-history-dto";

@Injectable({
  providedIn: "root"
})
export class ClientService {
  private clientUrl = "clients/";
  private clientV2Url = "v2/clients";
  cliendInfo$: BehaviorSubject<ClientDetailsDTO> = new BehaviorSubject(null);

  constructor(private http: HttpClient) { }

  listAll = (): Observable<ClientSimplifiedListDTO[]> => {
    return this.http.get<ClientSimplifiedListDTO[]>(
      environment.apiUrl + this.clientV2Url + "/allSimplified"
    );
  };

  findClientByTradingName(value: string): Observable<ClientDetailsDTO[]> {
    const params = new HttpParams().set("tradingName", value);

    return this.http.get<ClientDetailsDTO[]>(
      environment.microserviceSales + `${this.clientUrl}filters`,
      { params: params }
    );
  }

  listAllWithFilters = (
    searchFormSimplifiedListDTO: SearchFormSimplifiedListDTO
  ): Observable<ClientSimplifiedListDTO[]> => {
    let filters = "";
    if (searchFormSimplifiedListDTO.tradingName) {
      filters += `&tradingName=${searchFormSimplifiedListDTO.tradingName}`;
    }
    if (
      searchFormSimplifiedListDTO.isParent !== undefined &&
      searchFormSimplifiedListDTO.isParent !== null
    ) {
      filters += `&isParent=${searchFormSimplifiedListDTO.isParent}`;
    }

    return this.http.get<ClientSimplifiedListDTO[]>(
      environment.apiUrl + this.clientV2Url + `/allSimplified?${filters}`
    );
  };

  listAllWithFiltersNegotiation(
    searchFormSimplifiedListDTO: SearchFormSimplifiedListDTO,
    negotiationId: number
  ): Observable<ClientSimplifiedListDTO[]> {
    let filters = `negotiationId=${negotiationId}`;
    if (searchFormSimplifiedListDTO.tradingName) {
      filters += `&tradingName=${searchFormSimplifiedListDTO.tradingName}`;
    }
    return this.http.get<ClientSimplifiedListDTO[]>(
      environment.apiUrl + this.clientV2Url + `/allSimplified?${filters}`
    );
  }

  findByFilters(
    searchFormDTO: SearchFormDTO,
    pageManager: PageManager,
    sort = ""
  ): Observable<ClientDetailsDTO[]> {
    let filters = "";
    if (searchFormDTO.corporateName) {
      filters += `&tradingName=${searchFormDTO.corporateName}`;
    }
    if (searchFormDTO.tradingName) {
      filters += `&tradingName=${searchFormDTO.tradingName}`;
    }
    if (searchFormDTO.countryId) {
      filters += `&countryId=${searchFormDTO.countryId}`;
    }
    if (searchFormDTO.stateId) {
      filters += `&stateId=${searchFormDTO.stateId}`;
    }
    if (searchFormDTO.cityId) {
      filters += `&cityId=${searchFormDTO.cityId}`;
    }

    if (
      searchFormDTO.isParent !== undefined &&
      searchFormDTO.isParent !== null
    ) {
      filters += `&isParent=${searchFormDTO.isParent}`;
    }
    return this.http.get<ClientDetailsDTO[]>(
      environment.apiUrl +
      `${this.clientV2Url}?size=${pageManager.pageSize}&page=${pageManager.pageIndex}${filters}${sort}`
    );
  }

  save(saveClientDTO: SaveClientDTO): Observable<any> {
    return this.http.post<any>(
      environment.apiUrl + this.clientUrl,
      saveClientDTO
    );
  }

  delete(id: number): Observable<any> {
    return this.http.delete<any>(environment.apiUrl + `${this.clientUrl}${id}`);
  }

  findById(id: number): Observable<ClientDetailsDTO> {
    return this.http.get<ClientDetailsDTO>(
      environment.apiUrl + `${this.clientUrl}${id}`
    );
  }

  edit(id: number, saveClientDTO: SaveClientDTO): Observable<any> {
    return this.http.put<any>(
      environment.apiUrl + `${this.clientUrl}${id}`,
      saveClientDTO
    );
  }

  editWithKeycloak(id: number, saveClientDTO: SaveClientDTO): Observable<any> {
    return this.http.put<any>(
      environment.apiUrl + `${this.clientUrl}save-with-keycloak/${id}`,
      saveClientDTO
    );
  }

  findBlocksById(
    id: number,
    isParent?: boolean,
    ignoreBlocksAlreadyRegistered?: number
  ): Observable<BlockDTO[]> {
    let query = "?";
    if (isParent != undefined && isParent != null) {
      query += `&isParent=${isParent}`;
    }
    if (
      ignoreBlocksAlreadyRegistered != undefined &&
      ignoreBlocksAlreadyRegistered != null
    ) {
      query += `&ignoreBlocksAlreadyRegistered=${ignoreBlocksAlreadyRegistered}`;
    }
    return this.http.get<BlockDTO[]>(
      environment.apiUrl + `${this.clientUrl}${id}/blocks${query}`
    );
  }

  listBlockedCarworkshops(id: number): Observable<CarWorkshopDTO[]> {
    if (id != null) {
      return this.http.get<CarWorkshopDTO[]>(
        `${environment.apiUrl}${this.clientUrl}${id}/blocked-carworkshop`
      );
    } else {
      return null;
    }
  }

  updateBlockedCarworkshops(id: number, payload: any): Observable<any> {
    if (id != null) {
      return this.http.post<any>(
        `${environment.apiUrl}${this.clientUrl}${id}/blocked-carworkshop`,
        payload
      );
    } else {
      return null;
    }
  }

  getRequestingUsers(clientId: number) {
    return this.http.get<GetClientRequestingUserDTO[]>(
      `${environment.apiUrl}${this.clientUrl}${clientId}/requesting-users`
    );
  }

  updateRequestingUsers(
    clientId: number,
    users: PostClientRequestingUserDTO[]
  ): Observable<any> {
    return this.http.patch<any>(
      `${environment.apiUrl}${this.clientUrl}${clientId}/requesting-users`,
      users
    );
  }

  getApprovalUsers(clientId: number) {
    return this.http.get<GetClientApprovalUserDTO[]>(
      `${environment.apiUrl}${this.clientUrl}${clientId}/users-approval-permission`
    );
  }

  updateApprovalUsers(
    clientId: number,
    users: PostClientApprovalUserDTO[]
  ): Observable<any> {
    return this.http.patch<any>(
      `${environment.apiUrl}${this.clientUrl}${clientId}/users-approval-permission`,
      users
    );
  }

  hasPermission() {
    return this.http.get<boolean>(
      `${environment.apiUrl}${this.clientUrl}has-permission`
    );
  }

  getWorkflowApprovalLimits(
    clientId: number,
    limitNumber: number
  ): Observable<GetWorkflowApprovalLimitsDTO> {
    let params = new HttpParams();
    params = params.append("limitNumber", limitNumber.toString());

    return this.http.get<GetWorkflowApprovalLimitsDTO>(
      `${environment.apiUrl}${this.clientUrl}${clientId}/workflow-approval-limits`,
      { params }
    );
  }

  updateWorkflowConfigurations(
    body: UpdateWorkflowConfigurationsDTO
  ): Observable<void> {
    return this.http.patch<void>(
      `${environment.apiUrl}workflow-configurations`,
      body.configurations
    );
  }

  updateWorkflowLimitNumber(
    body: UpdateWorkflowApprovalLimitsDTO
  ): Observable<void> {
    return this.http.patch<void>(
      `${environment.apiUrl}workflow-configurations/limits`,
      body
    );
  }

  processCarworkshopWallet(file: File): Observable<WalletCarworkshopDTO> {
    const url = environment.apiUrl + this.clientV2Url + "/wallet-carworkshop";

    const body = new FormData();
    body.append("file", file);

    return this.http.post<WalletCarworkshopDTO>(url, body);
  }

  downloadCarworkshopWallet(clientId?: number): Observable<Blob> {
    const url = environment.apiUrl + this.clientV2Url + "/wallet-carworkshop";
    let params = new HttpParams();

    if (clientId) {
      params = params.append("clientId", clientId.toString());
    }

    return this.http.get(url, { params, responseType: "blob" });
  }

  getClientBillingHistory(
    clientId: number,
    pageManager: PageManager,
    sort = ""
  ): Observable<ClientBillingHistoryResponse> {
    return this.http.get<ClientBillingHistoryResponse>(
      `${environment.apiUrl}client-billing-history/${clientId}?size=${pageManager.pageSize}&page=${pageManager.pageIndex}${sort}`
    );
  }

  getClientFeeModelHistory(
    client_id: number,
    pageManager: PageManager,
    sort = ""
  ): Observable<ClientFeeHistoryResponse> {
    return this.http.get<ClientFeeHistoryResponse>(
      `${environment.apiUrl}client-fee-history/${client_id}?size=${pageManager.pageSize}&page=${pageManager.pageIndex}${sort}`
    );
  }

  getClientAdminFeeHistory(
    client_id: number,
    pageManager: PageManager,
    sort = ""
  ): Observable<ClientAdminFeeHistoryResponse> {
    return this.http.get<ClientAdminFeeHistoryResponse>(
      `${environment.apiUrl}client-fee-history/adm/${client_id}?size=${pageManager.pageSize}&page=${pageManager.pageIndex}${sort}`
    );
  }

  uploadContract(contractFile: File, clientId: number): Observable<any> {
    const formData = new FormData();
    formData.append('contractFile', contractFile);
    formData.append('clientId', clientId.toString());

    return this.http.post(`${environment.apiUrl}contract`, formData);
  }

  updateContract(contractFile: File, clientId: number): Observable<any> {
    const formData = new FormData();
    formData.append('contractFile', contractFile);
    formData.append('clientId', clientId.toString());

    return this.http.put(`${environment.apiUrl}contract`, formData);
  }

  downloadContract(contractFileId: number): Observable<string> {
    return this.http.get(`${environment.apiUrl}contract/${contractFileId}`, {
      responseType: 'text'
    });
  }


}
